import { useEffect, useState } from 'preact/hooks';
import type { CarProfile } from '../cars/CarProfile';
import { CARS } from '../cars/registry';
import { carSampleFiles, candidateNames } from '../audio/sampleFiles';
import { preferredFormats } from '../audio/EngineSampleManager';
import { requestGrant } from './grant';
import { isCarUnlocked } from './access';

/**
 * THE ENGINE LIVES ON THE DEVICE.
 *
 * A car with no coverage (a tunnel, a mountain road, a Tesla whose LTE has given up) still has GPS,
 * and GPS is all the engine needs to follow the road. What it cannot do without the network is fetch
 * its recordings, so they are fetched here, ahead of time, for every car this device owns: the same
 * files, in the same format order, that loadCarAudio asks for (sampleFiles.ts is the one list both
 * read). The service worker stores whatever passes through it under /audio/ keyed by path, so this
 * only has to ASK for each file once and the bank is on disk; after that the loader's own fetches
 * are answered from the cache, grant or no grant, network or no network.
 *
 * The shell (the HTML, the bundle, the worklets, the fonts, the photographs) is the service worker's
 * job at install (public/sw.js PRECACHE); this file is about the 10 MB per car that are the product.
 *
 * DEMO cars are not downloaded: their audio travels encrypted per token and the service worker never
 * keeps it, by design (nothing playable lands on disk). Offline is what the full engine buys.
 */
export type OfflineState =
  | { state: 'unknown' }
  | { state: 'checking' }
  | { state: 'downloading'; doneBytes: number; totalBytes: number; files: number; total: number }
  | { state: 'ready'; bytes: number }
  | { state: 'failed'; doneBytes: number; totalBytes: number }
  /** no network right now: it runs by itself when the browser says it is back */
  | { state: 'waiting' }
  /** nothing to do here: no service worker (dev, an old WebView), or a car that runs as a demo */
  | { state: 'unavailable' };

const RECORD_KEY = 'roardrive:offline:v1';
interface CarRecord { bytes: number; cache: string; missing: string[]; at: number }

const states = new Map<string, OfflineState>();
const listeners = new Set<() => void>();
const running = new Map<string, Promise<void>>();
const aborters = new Map<string, AbortController>();
let onlineHooked = false;

function set(carId: string, s: OfflineState): void {
  states.set(carId, s);
  // one quiet line per transition: the first question in the field is 'what did it think it was doing'
  try { if (s.state !== 'downloading') console.debug(`[offline] ${carId}: ${s.state}${'bytes' in s ? ` ${(s.bytes / 1e6).toFixed(1)} MB` : ''}`); } catch { /* ignore */ }
  for (const f of listeners) f();
}
export function offlineState(carId: string): OfflineState {
  return states.get(carId) ?? { state: 'unknown' };
}
/** components: repaint on every change of a car's offline state */
export function useOffline(carId: string): OfflineState {
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump((n) => n + 1);
    listeners.add(f);
    return () => { listeners.delete(f); };
  }, []);
  return offlineState(carId);
}

/** the service worker is what keeps the files; no worker, no offline (dev builds never register one) */
export function offlineSupported(): boolean {
  try {
    return import.meta.env.PROD && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof caches !== 'undefined';
  } catch {
    return false;
  }
}

/** true once a worker controls this page (its fetch handler is what stores the audio) */
async function controlled(): Promise<boolean> {
  if (!offlineSupported()) return false;
  if (navigator.serviceWorker.controller) return true;
  // The first visit on a slow LTE: main.tsx registers on window 'load', then the install fetches the
  // whole shell (a few MB) before `ready` resolves. That is a minute on a bad day, and a minute is
  // what this waits: giving up at fifteen seconds hid the row for the rest of the page's life.
  const ready = new Promise<boolean>((resolve) => {
    navigator.serviceWorker.ready.then(() => resolve(true), () => resolve(false));
  });
  const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 90_000));
  if (!(await Promise.race([ready, timeout]))) return false;
  // `ready` is the registration; the claim that makes this page ITS page follows a beat later
  for (let i = 0; i < 100 && !navigator.serviceWorker.controller; i++) await new Promise((r) => setTimeout(r, 100));
  return !!navigator.serviceWorker.controller;
}

/** the worker's audio cache, by its name pattern (public/sw.js: roardrive-audio-vNNN, the newest wins) */
async function audioCacheName(): Promise<string | null> {
  const keys = (await caches.keys()).filter((k) => /^roardrive-audio-v\d+$/.test(k));
  keys.sort((a, b) => Number(a.slice(17)) - Number(b.slice(17)));
  return keys.pop() ?? null;
}

function readRecords(): Record<string, CarRecord> {
  try { return JSON.parse(localStorage.getItem(RECORD_KEY) ?? '{}') as Record<string, CarRecord>; } catch { return {}; }
}
function writeRecord(carId: string, rec: CarRecord | null): void {
  try {
    const all = readRecords();
    if (rec) all[carId] = rec; else delete all[carId];
    localStorage.setItem(RECORD_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

const isHtml = (res: Response) => (res.headers.get('content-type') ?? '').includes('text/html');

/** what the build measured for this car: the bytes of the files the loader asks for, in its first-choice formats */
function expectedBytes(carId: string): number {
  try {
    const table = typeof __ROAR_AUDIO_BYTES__ !== 'undefined' ? __ROAR_AUDIO_BYTES__ : {};
    return table[carId] ?? 0;
  } catch { return 0; }
}

async function cachedFile(cache: Cache, base: string, names: string[]): Promise<Response | null> {
  for (const n of names) {
    const hit = await cache.match(`${base}/${n}`);
    if (hit && hit.ok && !isHtml(hit)) return hit;
  }
  return null;
}

/** the bytes of a cached response: the header when the server sent one, the body otherwise (once, then remembered) */
async function cachedBytes(res: Response): Promise<number> {
  const len = Number(res.headers.get('content-length'));
  if (len > 0) return len;
  try { return (await res.clone().blob()).size; } catch { return 0; }
}

/**
 * Make sure this car's bank is on the device: check the cache, download what is missing, say so.
 * One run per car at a time; a second call while one runs joins it. `force` retries a failed run.
 */
export function ensureOffline(profile: CarProfile, force = false): Promise<void> {
  const cur = offlineState(profile.id);
  if (!force && (cur.state === 'ready' || cur.state === 'unavailable')) return Promise.resolve();
  const inFlight = running.get(profile.id);
  if (inFlight) return inFlight;
  const p = run(profile).catch(() => undefined).finally(() => { running.delete(profile.id); aborters.delete(profile.id); });
  running.set(profile.id, p);
  return p;
}

/** an engine is starting: its loader wants the LTE for itself. Whatever was downloading stops; the bay asks again later. */
export function pauseOffline(): void {
  for (const [id, a] of aborters) { a.abort(); aborters.delete(id); if (offlineState(id).state === 'downloading') set(id, { state: 'unknown' }); }
}

function hookOnline(): void {
  if (onlineHooked || typeof window === 'undefined') return;
  onlineHooked = true;
  window.addEventListener('online', () => { void prefetchOwned(); });
}

async function run(profile: CarProfile): Promise<void> {
  const id = profile.id;
  hookOnline();
  if (!offlineSupported()) { set(id, { state: 'unavailable' }); return; }
  set(id, { state: 'checking' });
  if (!(await controlled())) { set(id, { state: 'unavailable' }); return; }

  const base = profile.samples.basePath.replace(/\/$/, '');
  const formats = preferredFormats();
  const files = carSampleFiles(profile).map((f) => ({ ...f, names: candidateNames(f.file, f.kind, formats) }));
  if (!files.length) { set(id, { state: 'unavailable' }); return; }

  // ---- already here? ----
  const cacheName = await audioCacheName();
  const rec = readRecords()[id];
  if (cacheName) {
    const cache = await caches.open(cacheName);
    const known = rec && rec.cache === cacheName ? rec : null;
    let bytes = 0;
    let complete = true;
    for (const f of files) {
      if (known?.missing.includes(f.file)) continue;
      const hit = await cachedFile(cache, base, f.names);
      if (!hit) { complete = false; break; }
      if (!known) bytes += await cachedBytes(hit);
    }
    if (complete) {
      const total = known ? known.bytes : bytes;
      if (!known) writeRecord(id, { bytes: total, cache: cacheName, missing: [], at: Date.now() });
      set(id, { state: 'ready', bytes: total });
      return;
    }
  }

  // ---- not (all) here: download ----
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { set(id, { state: 'waiting' }); return; }
  const grant = await requestGrant(id);
  if (!grant) { set(id, { state: 'waiting' }); return; }
  if (grant.mode !== 'full') { set(id, { state: 'unavailable' }); return; }
  const rev = typeof __ROAR_BUILD__ !== 'undefined' ? __ROAR_BUILD__ : 'dev';
  const q = (grant.token ? `?g=${encodeURIComponent(grant.token)}&` : '?') + `r=${encodeURIComponent(rev)}`;

  const ctrl = new AbortController();
  aborters.set(id, ctrl);
  let done = 0;
  let count = 0;
  let expected = Math.max(expectedBytes(id), 1);
  const missing: string[] = [];
  const progress = () => set(id, { state: 'downloading', doneBytes: done, totalBytes: Math.max(expected, done), files: count, total: files.length });
  progress();

  const one = async (f: (typeof files)[number]): Promise<void> => {
    for (const n of f.names) {
      const res = await fetch(`${base}/${n}${q}`, { signal: ctrl.signal });
      if (!res.ok || isHtml(res)) continue;   // a missing format answers with the home page; try the next one
      // read it to the end, so the worker's copy is complete and the bar moves with the bytes
      const reader = res.body?.getReader();
      if (reader) {
        for (;;) {
          const { done: end, value } = await reader.read();
          if (end) break;
          done += value.byteLength;
          progress();
        }
      } else {
        done += (await res.arrayBuffer()).byteLength;
        progress();
      }
      count++;
      progress();
      return;
    }
    // no format of this file on the server: the loader will not find it either, so it is not a failure
    missing.push(f.file);
    count++;
    progress();
  };

  try {
    // four at a time: enough to fill the pipe, not enough to starve the page
    const queue = [...files];
    await Promise.all(Array.from({ length: 4 }, async () => {
      for (let f = queue.shift(); f; f = queue.shift()) await one(f);
    }));
  } catch (err) {
    // a lost network, or pauseOffline() because an engine is starting; the bay asks again
    if (ctrl.signal.aborted) { set(id, { state: 'unknown' }); return; }
    try { console.debug(`[offline] ${id}: fetch failed: ${String(err)}`); } catch { /* ignore */ }
    set(id, { state: 'failed', doneBytes: done, totalBytes: Math.max(expected, done) });
    return;
  }

  // ---- and is it all there now? the worker decides what it kept, so ask it rather than assume. Its
  // cache.put runs beside the body this page just read and lands a beat after it, so a file that is not
  // there yet is asked for again, a few times, before it is called lost. ----
  let bytes = 0;
  for (let attempt = 0; ; attempt++) {
    bytes = 0;
    let short: string | null = null;
    const name = await audioCacheName();
    const cache = name ? await caches.open(name) : null;
    for (const f of files) {
      if (missing.includes(f.file)) continue;
      const hit = cache ? await cachedFile(cache, base, f.names) : null;
      if (!hit) { short = f.file; break; }
      bytes += await cachedBytes(hit);
    }
    if (!short) break;
    if (attempt >= 8) {
      try { console.debug(`[offline] ${id}: not in the cache after the download: ${short}`); } catch { /* ignore */ }
      set(id, { state: 'failed', doneBytes: done, totalBytes: Math.max(expected, done) });
      return;
    }
    await new Promise((r) => setTimeout(r, 500));
  }
  const name = (await audioCacheName())!;
  expected = bytes;
  writeRecord(id, { bytes, cache: name, missing, at: Date.now() });
  // ask the browser to keep it: an engine that was downloaded on purpose should not be the first thing evicted
  try { void navigator.storage?.persist?.(); } catch { /* ignore */ }
  set(id, { state: 'ready', bytes });
}

/** Every car this device owns, one after the other (the Home and the bay call this; so does coming back online). */
export async function prefetchOwned(): Promise<void> {
  if (!offlineSupported()) return;
  for (const car of CARS) {
    if (!car.available || !isCarUnlocked(car.id)) continue;
    await ensureOffline(car);
  }
}
